import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "components/common/ScrollToTop";
import ErrorBoundary from "components/common/ErrorBoundary";
import NotFound from "pages/NotFound";
import ProtectedRoute from "components/common/ProtectedRoute";
import { lazyImportWithRefresh } from './utils/lazyImportWithRefresh';
import { trackPageView } from "./lib/appInsights";
import HomePage from "pages/HomePage";

// Lazy-load all heavy pages — each only downloads when first navigated to
const Login = lazy(lazyImportWithRefresh(() => import('./pages/login'), 'login'));
const ChangePassword = lazy(lazyImportWithRefresh(() => import('./pages/change-password'), 'change-password'));
const Onboarding = lazy(lazyImportWithRefresh(() => import('./pages/onboarding'), 'onboarding'));
const ShopifyInstall = lazy(lazyImportWithRefresh(() => import('./pages/shopify-install'), 'shopify-install'));
const Dashboard = lazy(lazyImportWithRefresh(() => import('./pages/dashboard'), 'dashboard'));

// Lazy-load all heavy pages — each only downloads when first navigated to
const ManufacturingBuild = lazy(lazyImportWithRefresh(() => import('./pages/manufacturing-build'), 'manufacturing-build'));
const ProductsAndVariants = lazy(lazyImportWithRefresh(() => import('./pages/products-and-variants'), 'products-and-variants'));
const RawMaterialsInventory = lazy(lazyImportWithRefresh(() => import('./pages/raw-materials-inventory'), 'raw-materials-inventory'));
const BOMEditor = lazy(lazyImportWithRefresh(() => import('./pages/bom-editor'), 'bom-editor'));
const TeamManagement = lazy(lazyImportWithRefresh(() => import('./pages/team-management'), 'team-management'));
const AuditTrail = lazy(lazyImportWithRefresh(() => import('./pages/audit-trail'), 'audit-trail'));
const PurchaseOrders = lazy(lazyImportWithRefresh(() => import('./pages/purchase-orders'), 'purchase-orders'));
const HelpStuff = lazy(lazyImportWithRefresh(() => import('./pages/helpstuff'), 'help-stuff'));
const PrivacyPolicy = lazy(lazyImportWithRefresh(() => import('./pages/legal/PrivacyPolicy'), 'privacy-policy'));
const TermsAndConditions = lazy(lazyImportWithRefresh(() => import('./pages/legal/TermsAndConditions'), 'terms-and-conditions'));

// Billing pages — added 2026-03-04
// PricingPage is public (prospects can see pricing without login)
// BillingSettings is protected (requires auth to view team billing)
const PricingPage = lazy(lazyImportWithRefresh(() => import('./pages/billing/PricingPage'), 'pricing-page'));
const BillingSettings = lazy(lazyImportWithRefresh(() => import('./pages/billing'), 'billing-settings'));

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
      <p className="text-sm text-muted-foreground">Getting Stuff Done...</p>
    </div>
  </div>
);

const RecoveryRedirectGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const isResetRoute = location?.pathname === '/reset-password' || location?.pathname === '/change-password';
    if (isResetRoute) {
      return;
    }

    const searchParams = new URLSearchParams(location?.search || '');
    const hash = (location?.hash || '').replace(/^#/, '');
    const hashParams = new URLSearchParams(hash);

    // Only redirect for password recovery flows — NOT magic links or other auth types
    const hashType = hashParams.get('type');
    const searchType = searchParams.get('type');
    const isRecoveryFlow =
      searchType === 'recovery' ||
      hashType === 'recovery';

    if (!isRecoveryFlow) {
      return;
    }

    const search = location?.search || '';
    const hashSuffix = location?.hash || '';
    navigate(`/reset-password${search}${hashSuffix}`, { replace: true });
  }, [location?.pathname, location?.search, location?.hash, navigate]);

  return null;
};

const AppInsightsRouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const pageName = location?.pathname || '/';
    const pageUrl = typeof window !== 'undefined'
      ? window.location.href
      : pageName;

    trackPageView({
      name: pageName,
      uri: pageUrl,
    });
  }, [location?.pathname, location?.search, location?.hash]);

  return null;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <RecoveryRedirectGuard />
      <AppInsightsRouteTracker />
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
      <RouterRoutes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/reset-password" element={<ChangePassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/shopify-install" element={<ShopifyInstall />} />
        <Route path="/pricing" element={<PricingPage />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/bom-editor" element={<ProtectedRoute><BOMEditor /></ProtectedRoute>} />
        <Route path="/manufacturing-build" element={<ProtectedRoute><ManufacturingBuild /></ProtectedRoute>} />
        <Route path="/products-and-variants" element={<ProtectedRoute><ProductsAndVariants /></ProtectedRoute>} />
        <Route path="/raw-materials-inventory" element={<ProtectedRoute><RawMaterialsInventory /></ProtectedRoute>} />
        <Route path="/purchase-orders" element={<ProtectedRoute><PurchaseOrders /></ProtectedRoute>} />
        <Route path="/team-management" element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
        <Route path="/audit-trail" element={<ProtectedRoute><AuditTrail /></ProtectedRoute>} />
        <Route path="/helpstuff" element={<ProtectedRoute><HelpStuff /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingSettings /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
